import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, ArrowLeft, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, { withCredentials: true });
      setInquiries(data);
    } catch {
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setUser(data);
        loadInquiries();
      })
      .catch(() => setUser(false));
  }, [loadInquiries]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      loadInquiries();
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  if (user === null) {
    return <div data-testid="admin-loading" className="min-h-screen bg-forest flex items-center justify-center text-cream font-sans text-sm tracking-[0.2em] uppercase">Loading…</div>;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-forest flex items-center justify-center px-6">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-md bg-cream p-10 rounded-sm">
          <p className="font-sans text-xs tracking-[0.25em] uppercase text-brass font-semibold mb-3">Private Access</p>
          <h1 className="font-serif text-4xl text-forest mb-8">Inquiry Dashboard</h1>
          {error && <p data-testid="admin-login-error" className="mb-4 font-sans text-sm text-red-800">{error}</p>}
          <label className="block font-sans text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">Email</label>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-5 border border-border bg-white px-4 py-3 rounded-sm font-sans text-sm text-forest focus:outline-none focus:ring-1 focus:ring-brass"
          />
          <label className="block font-sans text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2">Password</label>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-8 border border-border bg-white px-4 py-3 rounded-sm font-sans text-sm text-forest focus:outline-none focus:ring-1 focus:ring-brass"
          />
          <button data-testid="admin-login-btn" type="submit" className="w-full bg-forest text-cream font-sans text-sm font-semibold tracking-[0.15em] uppercase py-4 rounded-sm transition-colors duration-300 hover:bg-[#1f3d31]">
            Sign In
          </button>
          <Link to="/" data-testid="admin-back-link" className="mt-6 flex items-center gap-2 font-sans text-xs text-muted-foreground hover:text-brass transition-colors duration-300">
            <ArrowLeft size={14} /> Back to BuildInWoodlake.com
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-sans text-xs tracking-[0.25em] uppercase text-brass font-semibold">BuildInWoodlake.com</p>
            <h1 data-testid="admin-dashboard-title" className="font-serif text-3xl sm:text-4xl mt-1">Inquiry Dashboard</h1>
            <p className="font-sans text-xs text-cream/60 mt-1">Signed in as {user.name || user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={loadInquiries} className="flex items-center gap-2 border border-cream/30 text-cream font-sans text-xs tracking-wide px-5 py-3 rounded-sm hover:bg-cream/10 transition-colors duration-300">
              <RefreshCw size={14} /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 bg-brass text-forest font-sans text-xs font-semibold tracking-wide px-5 py-3 rounded-sm hover:bg-[#cbaa66] transition-colors duration-300">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <p data-testid="admin-inquiry-count" className="font-sans text-sm text-muted-foreground mb-8">
          {loading ? "Loading…" : `${inquiries.length} ${inquiries.length === 1 ? "inquiry" : "inquiries"} received`}
        </p>
        {inquiries.length === 0 && !loading ? (
          <div data-testid="admin-empty-state" className="border border-border bg-white p-16 text-center rounded-sm">
            <p className="font-serif text-2xl text-forest">No inquiries yet</p>
            <p className="font-sans text-sm text-muted-foreground mt-2">New requests from the website will appear here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {inquiries.map((inq) => (
              <article key={inq.id} data-testid={`admin-inquiry-${inq.id}`} className="bg-white border border-border rounded-sm p-6 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="font-serif text-2xl text-forest">{inq.first_name} {inq.last_name}</h2>
                    <p className="font-sans text-xs text-muted-foreground mt-1">{formatDate(inq.created_at)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {inq.inquiry_type === "Showing Request" && (
                      <span data-testid={`admin-showing-badge-${inq.id}`} className="bg-forest text-cream font-sans text-[11px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-sm">
                        Showing · {inq.preferred_date}{inq.preferred_time ? ` · ${inq.preferred_time}` : ""}
                      </span>
                    )}
                    <span className="bg-brass/15 text-forest border border-brass/40 font-sans text-[11px] font-semibold tracking-[0.15em] uppercase px-3 py-1.5 rounded-sm">
                      {inq.lot_of_interest}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-8 gap-y-2 font-sans text-sm text-forest/80 mb-4">
                  <a href={`mailto:${inq.email}`} className="flex items-center gap-2 hover:text-brass transition-colors duration-300"><Mail size={14} className="text-brass" /> {inq.email}</a>
                  {inq.phone && <a href={`tel:${inq.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 hover:text-brass transition-colors duration-300"><Phone size={14} className="text-brass" /> {inq.phone}</a>}
                  <span className="text-muted-foreground">Prefers: {inq.preferred_contact}</span>
                  {inq.working_with_agent && <span className="text-muted-foreground">Agent: {inq.working_with_agent}</span>}
                </div>
                {inq.message && <p className="font-sans text-sm leading-relaxed text-muted-foreground border-t border-border pt-4">{inq.message}</p>}
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
